import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import AuthImage from "../components/app/AuthImage";
import { Star, MapPin, Clock, Phone, Sparkles, ArrowLeft, MessageSquare, ShieldCheck, Award, Trophy, Zap, MessageCircle, Instagram } from "lucide-react";
import Logo from "../components/app/Logo";
import ReportButton from "../components/app/ReportButton";
import FavoriteButton from "../components/app/FavoriteButton";
import ContactActions from "../components/app/ContactActions";
import { SPECIALTY_LABEL } from "./FindCoaches";

const BADGE_LABEL = {
  top_rated: { label: "TOP RATED", icon: Star, color: "tag-pitch" },
  uefa_a: { label: "UEFA A", icon: ShieldCheck, color: "" },
  uefa_b: { label: "UEFA B", icon: ShieldCheck, color: "" },
  uefa_pro: { label: "UEFA PRO", icon: ShieldCheck, color: "tag-pitch" },
  elite: { label: "ELITE", icon: Award, color: "tag-pitch" },
  verified: { label: "VERIFIED", icon: ShieldCheck, color: "tag-pitch" },
};

function StarRating({ value, onChange, readOnly = false, size = "md", testId }) {
  const sz = size === "lg" ? "w-6 h-6" : "w-4 h-4";
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = (value || 0) >= i;
        return (
          <button
            key={i}
            type="button"
            disabled={readOnly}
            onClick={() => onChange?.(i)}
            className={`${readOnly ? "" : "hover:scale-110"} transition-transform`}
            data-testid={`star-${i}`}
          >
            <Star className={`${sz} ${filled ? "text-pitch fill-pitch" : "text-white/20"}`} />
          </button>
        );
      })}
    </div>
  );
}

export default function CoachPublicProfile() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [eligibility, setEligibility] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/public/coaches/${id}`);
    setData(data);
    if (user?.role === "parent") {
      api.get(`/coaches/${id}/reviews/eligibility`).then((r) => {
        setEligibility(r.data);
        if (r.data.existing) {
          setReviewForm({ rating: r.data.existing.rating, comment: r.data.existing.comment || "" });
        }
      }).catch(() => {});
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="pitch-loader"><div className="ball" /></div>
      </div>
    );
  }

  const c = data.coach;
  const reviews = data.reviews || [];

  const submitReview = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await api.post(`/coaches/${id}/reviews`, reviewForm);
      await load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    }
    setSubmitting(false);
  };

  const messageCoach = async () => {
    if (!user) { nav("/login"); return; }
    try {
      const { data } = await api.post("/chat/threads", { other_user_id: id });
      nav("/chat");
    } catch (_) { nav("/chat"); }
  };

  return (
    <div className="min-h-screen pb-16">
      {/* Public top bar */}
      <header className="glass-strong sticky top-0 z-30 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between p-4">
          <Link to="/" className="flex items-center">
            <Logo size={26} wordmark />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/find-coaches" className="text-xs font-mono uppercase tracking-widest hover:text-pitch transition-colors flex items-center gap-1">
              <ArrowLeft className="w-3 h-3" /> {lang === "bg" ? "Всички треньори" : "All coaches"}
            </Link>
            {!user && (
              <Link to="/login" className="btn-primary px-3 py-1.5 text-xs">{t("login")}</Link>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {/* Hero */}
        <div className="card-sharp p-6 md:p-8 relative overflow-hidden grain animate-fade-up" style={{ opacity: 0 }}>
          <div className="absolute inset-0 pitch-field opacity-40" />
          <div className="relative flex flex-col md:flex-row items-start gap-6">
            <div className="w-32 h-32 md:w-40 md:h-40 flex-shrink-0 relative">
              {c.photo_url ? (
                <AuthImage src={c.photo_url} className="w-full h-full object-cover border-2 border-pitch"
                  fallback={<div className="w-full h-full bg-pitch text-white font-oswald flex items-center justify-center text-6xl">{c.name?.[0]}</div>} />
              ) : (
                <div className="w-full h-full bg-pitch text-white font-oswald flex items-center justify-center text-6xl">{c.name?.[0]}</div>
              )}
              {c.is_pro && (
                <div className="absolute -bottom-2 -right-2 tag tag-pitch bg-[#0A1628] flex items-center gap-1">
                  <Sparkles className="w-2.5 h-2.5" /> PRO
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-mono text-pitch mb-1">// COACH PROFILE</div>
              <h1 className="font-oswald uppercase text-4xl md:text-5xl tracking-tight leading-none">{c.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <div className="flex items-center gap-1.5">
                  <StarRating value={c.rating || 0} readOnly />
                  <span className="font-oswald text-xl">{(c.rating || 0).toFixed(1)}</span>
                  <span className="text-xs text-white/40">({c.review_count || 0})</span>
                </div>
                {c.city && (
                  <span className="text-white/60 flex items-center gap-1 text-sm"><MapPin className="w-3.5 h-3.5 text-pitch" />{c.city}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {c.specialty && <span className="chip-badge">{SPECIALTY_LABEL(c.specialty, lang)}</span>}
                {(c.badges || []).map((b) => {
                  const def = BADGE_LABEL[b];
                  if (!def) return null;
                  const Icon = def.icon;
                  const cls = b === "uefa_pro" ? "gold" : b === "top_rated" ? "amber" : b === "elite" ? "platinum" : "";
                  return (
                    <span key={b} className={`chip-badge ${cls}`} data-testid={`badge-${b}`}>
                      <Icon className="w-3 h-3" /> {def.label}
                    </span>
                  );
                })}
                {c.years_experience != null && <span className="chip-badge">{c.years_experience}+ {lang === "bg" ? "години" : "years"}</span>}
                {c.hourly_rate != null && <span className="chip-badge gold">€{c.hourly_rate}/{lang === "bg" ? "ч" : "h"}</span>}
                {c.instant_book_enabled && (
                  <span className="chip-badge gold flex items-center gap-1" data-testid="profile-instant-badge" title={lang === "bg" ? "Авто-потвърждавани сесии" : "Auto-confirmed sessions"}>
                    <Zap className="w-3 h-3" /> INSTANT
                  </span>
                )}
              </div>
              {c.bio && <p className="text-white/70 mt-4 max-w-2xl">{c.bio}</p>}
              {c.available_hours && (
                <div className="mt-3 flex items-center gap-2 text-sm text-white/60">
                  <Clock className="w-4 h-4 text-pitch" /> {c.available_hours}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <button onClick={messageCoach} className="btn-primary px-5 py-3 flex items-center justify-center gap-2" data-testid="contact-coach">
                <MessageSquare className="w-4 h-4" /> {lang === "bg" ? "Свържи се" : "Contact"}
              </button>
              <ContactActions
                phone={c.phone}
                hasPhone={!!c.has_phone}
                whatsapp={c.whatsapp}
                viber={c.viber}
                instagram={c.instagram}
                revealed={!!c.contact_revealed}
                lang={lang}
                className="justify-center"
              />
              <div className="flex justify-center items-center gap-2">
                <FavoriteButton coachId={c.id} variant="chip" />
                <ReportButton reportedId={c.id} reportedName={c.name} context="profile" variant="ghost" testIdSuffix="profile" />
              </div>
            </div>
          </div>
        </div>

        {/* Reviews + Booking */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {(c.gallery_urls || []).length > 0 && (
              <div className="card-sharp p-6" data-testid="coach-gallery">
                <h2 className="font-oswald uppercase text-2xl tracking-tight mb-4">{lang === "bg" ? "Галерия" : "Gallery"}</h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {(c.gallery_urls || []).map((u, i) => (
                    <a key={i} href={u + (u.startsWith("/api") ? `?auth=${localStorage.getItem("auth_token") || ""}` : "")} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden border border-white/10 hover:border-pitch/50 transition-colors group">
                      <img src={u + (u.startsWith("/api") ? `?auth=${localStorage.getItem("auth_token") || ""}` : "")} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {((c.languages_spoken || []).length > 0 || (c.achievements || []).length > 0) && (
              <div className="card-sharp p-6 space-y-5" data-testid="coach-extras">
                {(c.languages_spoken || []).length > 0 && (
                  <div>
                    <div className="label-mono mb-2">// {lang === "bg" ? "Езици" : "Languages"}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(c.languages_spoken || []).map((l, i) => <span key={i} className="chip-badge">{l}</span>)}
                    </div>
                  </div>
                )}
                {(c.achievements || []).length > 0 && (
                  <div>
                    <div className="label-mono mb-2">// {lang === "bg" ? "Постижения" : "Achievements"}</div>
                    <ul className="space-y-2">
                      {(c.achievements || []).map((a, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                          <span className="text-pitch mt-0.5">▸</span> {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="card-sharp p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-oswald uppercase text-2xl tracking-tight">{lang === "bg" ? "Отзиви" : "Reviews"}</h2>
                <span className="label-mono">{reviews.length}</span>
              </div>
              {reviews.length === 0 ? (
                <div className="py-10 text-center text-white/30 text-xs font-mono tracking-widest">
                  {lang === "bg" ? "Все още няма отзиви" : "No reviews yet"}
                </div>
              ) : (
                <div className="space-y-3">
                  {reviews.map((r) => (
                    <div key={r.id} className="border border-white/5 p-4" data-testid={`review-${r.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-pitch/15 text-pitch font-oswald flex items-center justify-center">{r.parent_name?.[0]}</div>
                          <div>
                            <div className="font-oswald uppercase tracking-tight text-sm">{r.parent_name}</div>
                            <div className="text-[10px] font-mono text-white/30">{new Date(r.created_at).toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US")}</div>
                          </div>
                        </div>
                        <StarRating value={r.rating} readOnly />
                      </div>
                      {r.comment && <p className="text-sm text-white/70 mt-2">{r.comment}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>

          {/* Review form (parents only, eligible) — moved INSIDE col-span-2 block */}
            <div className="card-sharp p-6">
              <h3 className="font-oswald uppercase text-xl tracking-tight mb-3">
                {eligibility?.existing ? (lang === "bg" ? "Редактирай отзив" : "Edit review") : (lang === "bg" ? "Остави отзив" : "Leave a review")}
              </h3>
              {!user ? (
                <div className="text-sm text-white/50">{lang === "bg" ? "Влезте, за да оцените" : "Log in to review"}.</div>
              ) : user.role !== "parent" ? (
                <div className="text-sm text-white/50">{lang === "bg" ? "Само родители могат да оценяват" : "Parents only"}</div>
              ) : !eligibility?.eligible ? (
                <div className="text-sm text-white/50">{lang === "bg" ? "Детето ви трябва да е посетило тренировка на този треньор, за да оставите отзив." : "Your child must attend a training of this coach to leave a review."}</div>
              ) : (
                <form onSubmit={submitReview} className="space-y-3" data-testid="review-form">
                  <div>
                    <div className="label-mono mb-2">{lang === "bg" ? "Оценка" : "Rating"}</div>
                    <StarRating value={reviewForm.rating} onChange={(v) => setReviewForm({ ...reviewForm, rating: v })} size="lg" testId="rating-input" />
                  </div>
                  <div>
                    <div className="label-mono mb-1">{lang === "bg" ? "Коментар" : "Comment"}</div>
                    <textarea
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                      rows={4}
                      className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none text-sm"
                      data-testid="review-comment"
                    />
                  </div>
                  {err && <div className="text-red-400 text-sm font-mono">{err}</div>}
                  <button type="submit" disabled={submitting} className="w-full btn-primary py-2.5" data-testid="review-submit">
                    {eligibility?.existing ? (lang === "bg" ? "Обнови" : "Update") : (lang === "bg" ? "Изпрати" : "Submit")}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Right column — Contact + Booking (sticky on desktop) */}
          <aside className="lg:sticky lg:top-24 self-start space-y-4">
            <ContactPanel coach={c} lang={lang} />
            <BookingWidget coach={c} lang={lang} user={user} />
          </aside>
        </div>
      </div>
    </div>
  );
}

function ContactPanel({ coach, lang }) {
  const hasPhone = !!coach.phone;
  const hasWa = !!coach.whatsapp;
  const hasViber = !!coach.viber;
  const hasIg = !!coach.instagram;
  const hasAny = hasPhone || hasWa || hasViber || hasIg;
  const lockedPhone = !coach.contact_revealed && coach.has_phone && !hasPhone;
  const lockedWa = !coach.contact_revealed && coach.has_whatsapp && !hasWa;
  const lockedViber = !coach.contact_revealed && coach.has_viber && !hasViber;
  const anyLocked = lockedPhone || lockedWa || lockedViber;

  if (!hasAny && !anyLocked) return null;
  return (
    <div className="card-sharp p-5 sm:p-6 rounded-lg" data-testid="coach-contact-panel">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-pitch" />
        <h3 className="font-oswald uppercase text-lg sm:text-xl tracking-tight">
          {lang === "bg" ? "Свържи се директно" : "Contact directly"}
        </h3>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {hasPhone ? (
          <a href={`tel:${coach.phone}`} className="flex flex-col items-center gap-1 p-3 border border-white/10 hover:border-pitch/60 hover:bg-pitch/5 rounded-md transition-all group" data-testid="coach-contact-call">
            <span className="w-9 h-9 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center group-hover:bg-pitch group-hover:text-black transition-colors"><Phone className="w-4 h-4" /></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/65">{lang === "bg" ? "Звънни" : "Call"}</span>
          </a>
        ) : lockedPhone && (
          <div className="flex flex-col items-center gap-1 p-3 border border-dashed border-white/10 rounded-md opacity-50" title={lang === "bg" ? "Достъпно след резервация" : "After booking"} data-testid="coach-contact-call-locked">
            <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><Phone className="w-4 h-4 text-white/35" /></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/35">{lang === "bg" ? "Заключен" : "Locked"}</span>
          </div>
        )}
        {hasWa ? (
          <a href={`https://wa.me/${coach.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 p-3 border border-white/10 hover:border-blue-400/60 hover:bg-blue-400/5 rounded-md transition-all group" data-testid="coach-contact-whatsapp">
            <span className="w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-black transition-colors"><MessageCircle className="w-4 h-4 text-blue-400 group-hover:text-black" /></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/65">WhatsApp</span>
          </a>
        ) : lockedWa && (
          <div className="flex flex-col items-center gap-1 p-3 border border-dashed border-white/10 rounded-md opacity-50" data-testid="coach-contact-whatsapp-locked">
            <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><MessageCircle className="w-4 h-4 text-white/35" /></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/35">WhatsApp</span>
          </div>
        )}
        {hasViber ? (
          <a href={`viber://chat?number=${encodeURIComponent(coach.viber)}`} className="flex flex-col items-center gap-1 p-3 border border-white/10 hover:border-purple-400/60 hover:bg-purple-400/5 rounded-md transition-all group" data-testid="coach-contact-viber">
            <span className="w-9 h-9 rounded-full bg-purple-500/10 border border-purple-500/30 flex items-center justify-center group-hover:bg-purple-500 group-hover:text-white transition-colors"><MessageSquare className="w-4 h-4 text-purple-400 group-hover:text-white" /></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/65">Viber</span>
          </a>
        ) : lockedViber && (
          <div className="flex flex-col items-center gap-1 p-3 border border-dashed border-white/10 rounded-md opacity-50" data-testid="coach-contact-viber-locked">
            <span className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><MessageSquare className="w-4 h-4 text-white/35" /></span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-white/35">Viber</span>
          </div>
        )}
      </div>
      {hasIg && (
        <a href={coach.instagram.startsWith("http") ? coach.instagram : `https://instagram.com/${coach.instagram.replace(/^@/, "")}`} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center justify-center gap-2 py-2 border border-white/10 hover:border-pink-400/55 hover:bg-pink-400/5 rounded-md transition-colors text-sm" data-testid="coach-contact-instagram">
          <Instagram className="w-4 h-4 text-pink-400" />
          <span className="font-mono text-xs tracking-wider">{coach.instagram}</span>
        </a>
      )}
      {anyLocked && !hasAny && (
        <div className="text-[11px] text-white/45 font-mono mt-3 flex items-start gap-1.5">
          <span>ⓘ</span>
          <span>{lang === "bg" ? "Контактите се отключват след резервация." : "Contacts unlock after booking."}</span>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-white/5 text-center">
        <span className="text-[11px] font-mono uppercase tracking-widest text-white/45">
          {lang === "bg" ? "или резервирай долу ↓" : "or book a session below ↓"}
        </span>
      </div>
    </div>
  );
}

function BookingWidget({ coach, lang, user }) {
  const [slot, setSlot] = useState("");
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [instantSlots, setInstantSlots] = useState([]);
  const [pickedInstantIdx, setPickedInstantIdx] = useState(null);
  const [mode, setMode] = useState(coach.instant_book_enabled ? "instant" : "custom");
  const [payMethod, setPayMethod] = useState(coach.accepts_card !== false ? "card" : "cash");
  const amount = (Number(coach.hourly_rate) || 0) * (duration / 60);

  useEffect(() => {
    if (!coach.instant_book_enabled) return;
    api.get(`/public/coaches/${coach.id}/instant-slots?weeks=3`)
      .then((r) => setInstantSlots(r.data || []))
      .catch(() => setInstantSlots([]));
  }, [coach.id, coach.instant_book_enabled]);

  const book = async (e) => {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      let body;
      if (mode === "instant" && pickedInstantIdx != null) {
        const s = instantSlots[pickedInstantIdx];
        if (!s) throw new Error("Pick a slot");
        body = {
          coach_id: coach.id,
          slot_at: s.slot_at,
          duration_min: s.duration_min,
          notes,
          origin_url: window.location.origin,
          instant: true,
          payment_method: "card",  // Instant Book requires card
        };
      } else {
        if (!slot) throw new Error("Pick a date");
        body = {
          coach_id: coach.id,
          slot_at: new Date(slot).toISOString(),
          duration_min: Number(duration),
          notes,
          origin_url: window.location.origin,
          payment_method: payMethod,
        };
      }
      const { data } = await api.post("/bookings", body);
      if (data.checkout_url) {
        // Card flow — redirect to Stripe
        window.location.href = data.checkout_url;
      } else {
        // Cash flow — booking is pending_cash, navigate to bookings list
        window.location.href = "/bookings?cash_pending=1";
      }
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    }
    setBusy(false);
  };

  const instantSlotAmount = pickedInstantIdx != null && instantSlots[pickedInstantIdx]
    ? (Number(coach.hourly_rate) || 0) * (instantSlots[pickedInstantIdx].duration_min / 60)
    : null;

  return (
    <div className="card-sharp p-6" data-testid="booking-widget">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-pitch" />
        <h3 className="font-oswald uppercase text-xl tracking-tight">{lang === "bg" ? "Резервирай сесия" : "Book a session"}</h3>
      </div>
      {!user || user.role !== "parent" ? (
        <div className="space-y-3" data-testid="booking-cta-non-parent">
          <div className="text-sm text-white/60">
            {!user
              ? (lang === "bg" ? "Влез като родител, за да резервираш сесия." : "Log in as a parent to book a session.")
              : (lang === "bg" ? "Само родителски акаунти могат да резервират." : "Only parent accounts can book sessions.")}
          </div>
          {!user && (
            <Link to="/login" className="btn-primary py-2.5 px-4 inline-flex items-center gap-2 text-sm" data-testid="booking-login-cta">
              <Sparkles className="w-4 h-4" />
              {lang === "bg" ? "Вход / Регистрация" : "Log in / Sign up"}
            </Link>
          )}
        </div>
      ) : (
        <>
          {coach.instant_book_enabled && (
            <div className="flex items-center gap-1 mb-4 bg-white/[0.03] p-1 rounded-md" data-testid="booking-mode-tabs">
              <button
                type="button"
                onClick={() => { setMode("instant"); setErr(""); }}
                className={`flex-1 py-2 px-3 text-xs font-mono uppercase tracking-widest rounded transition-colors flex items-center justify-center gap-1.5 ${mode === "instant" ? "bg-gold text-black" : "text-white/55 hover:text-white"}`}
                style={mode === "instant" ? { background: "#FFC857", color: "#000" } : {}}
                data-testid="booking-mode-instant"
              >
                <Zap className="w-3 h-3" /> Instant
              </button>
              <button
                type="button"
                onClick={() => { setMode("custom"); setErr(""); }}
                className={`flex-1 py-2 px-3 text-xs font-mono uppercase tracking-widest rounded transition-colors ${mode === "custom" ? "bg-pitch text-white" : "text-white/55 hover:text-white"}`}
                data-testid="booking-mode-custom"
              >
                {lang === "bg" ? "Поискай" : "Request"}
              </button>
            </div>
          )}

          {mode === "instant" && coach.instant_book_enabled ? (
            <form onSubmit={book} className="space-y-3" data-testid="instant-form">
              <div className="text-xs text-white/55 leading-relaxed flex items-start gap-1.5">
                <Zap className="w-3.5 h-3.5 text-gold flex-shrink-0 mt-0.5" />
                <span>{lang === "bg" ? "Тези слотове се потвърждават автоматично след плащане. Без чакане." : "These slots auto-confirm the moment payment lands. No waiting."}</span>
              </div>
              {instantSlots.length === 0 ? (
                <div className="text-center py-6 border border-dashed border-white/10 rounded-md" data-testid="instant-no-slots">
                  <Clock className="w-6 h-6 mx-auto mb-2 text-white/25" />
                  <div className="text-sm text-white/55">{lang === "bg" ? "Няма свободни Instant слотове" : "No instant slots available"}</div>
                  <button type="button" onClick={() => setMode("custom")} className="text-xs text-pitch hover:underline mt-2">
                    {lang === "bg" ? "Поискай нестандартен час" : "Request a custom time"}
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto scroll-thin pr-1">
                  {instantSlots.slice(0, 8).map((s, i) => {
                    const dt = new Date(s.slot_at);
                    const dateLabel = dt.toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US", { weekday: "short", day: "numeric", month: "short" });
                    const timeLabel = dt.toLocaleTimeString(lang === "bg" ? "bg-BG" : "en-US", { hour: "2-digit", minute: "2-digit" });
                    const active = pickedInstantIdx === i;
                    return (
                      <button
                        key={s.slot_at}
                        type="button"
                        onClick={() => setPickedInstantIdx(i)}
                        className={`w-full text-left p-3 border transition-colors flex items-center justify-between gap-2 rounded-md ${active ? "border-gold bg-gold/10" : "border-white/8 hover:border-pitch/40"}`}
                        data-testid={`instant-slot-${i}`}
                        style={active ? { borderColor: "#FFC857", background: "rgba(255,200,87,0.08)" } : {}}
                      >
                        <div>
                          <div className="font-oswald uppercase tracking-tight text-sm">{dateLabel}</div>
                          <div className="text-xs text-white/55 font-mono">{timeLabel} · {s.duration_min} {lang === "bg" ? "мин" : "min"}</div>
                        </div>
                        <Zap className="w-4 h-4" style={{ color: active ? "#FFC857" : "rgba(255,255,255,0.35)" }} />
                      </button>
                    );
                  })}
                </div>
              )}
              <div>
                <label className="label-mono block mb-1.5">{lang === "bg" ? "Бележки" : "Notes"}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none text-sm"
                  data-testid="instant-notes"
                />
              </div>
              {instantSlotAmount != null && (
                <div className="flex items-center justify-between pt-2 border-t border-white/5">
                  <span className="label-mono">{lang === "bg" ? "Цена" : "Total"}</span>
                  <span className="font-oswald text-3xl" style={{ color: "#FFC857" }}>€{instantSlotAmount.toFixed(2)}</span>
                </div>
              )}
              {err && <div className="text-red-400 text-xs font-mono" data-testid="instant-error">{err}</div>}
              <button
                type="submit"
                disabled={busy || pickedInstantIdx == null}
                className="w-full py-3 flex items-center justify-center gap-2 font-oswald uppercase tracking-wider text-black font-semibold rounded-md disabled:opacity-50 transition-all hover:scale-[1.01]"
                style={{ background: "linear-gradient(135deg, #FFD96B 0%, #FFC857 100%)", boxShadow: "0 8px 24px -8px rgba(255,200,87,0.45)" }}
                data-testid="instant-submit"
              >
                <Zap className="w-4 h-4" />
                {busy ? "..." : (lang === "bg" ? "Instant Book & Плати" : "Instant Book & Pay")}
              </button>
              <div className="text-[10px] font-mono text-white/30 text-center">
                {lang === "bg" ? "Авто-потвърждаване · Сигурно плащане през Stripe" : "Auto-confirm · Secure payment via Stripe"}
              </div>
            </form>
          ) : (
            <form onSubmit={book} className="space-y-3">
              <div>
                <label className="label-mono block mb-1.5">{lang === "bg" ? "Дата и час" : "Date & time"}</label>
                <input
                  type="datetime-local"
                  value={slot}
                  onChange={(e) => setSlot(e.target.value)}
                  required
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
                  data-testid="booking-slot"
                />
              </div>
              <div>
                <label className="label-mono block mb-1.5">{lang === "bg" ? "Продължителност" : "Duration"}</label>
                <div className="grid grid-cols-3 gap-2">
                  {[30, 60, 90].map((m) => (
                    <button
                      type="button"
                      key={m}
                      onClick={() => setDuration(m)}
                      className={`py-2 border font-oswald uppercase tracking-wider text-sm ${duration === m ? "border-pitch bg-pitch text-white" : "border-white/10 text-white/60 hover:border-white/30"}`}
                      data-testid={`booking-dur-${m}`}
                    >
                      {m} {lang === "bg" ? "мин" : "min"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1.5">{lang === "bg" ? "Бележки" : "Notes"}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none text-sm"
                  data-testid="booking-notes"
                />
              </div>
              {/* Payment method — Iter 24 */}
              {(coach.accepts_card !== false || coach.accepts_cash) && (
                <div data-testid="booking-paymethod">
                  <label className="label-mono block mb-1.5">{lang === "bg" ? "Метод на плащане" : "Payment method"}</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPayMethod("card")}
                      disabled={coach.accepts_card === false}
                      className={`py-2.5 px-3 border rounded-md font-oswald uppercase tracking-wider text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${payMethod === "card" ? "border-pitch bg-pitch/10 text-pitch" : "border-white/10 text-white/60 hover:border-white/30"}`}
                      data-testid="paymethod-card"
                    >
                      💳 {lang === "bg" ? "Карта" : "Card"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPayMethod("cash")}
                      disabled={!coach.accepts_cash}
                      title={!coach.accepts_cash ? (lang === "bg" ? "Този треньор не приема кеш" : "This coach does not accept cash") : undefined}
                      className={`py-2.5 px-3 border rounded-md font-oswald uppercase tracking-wider text-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${payMethod === "cash" ? "border-pitch bg-pitch/10 text-pitch" : "border-white/10 text-white/60 hover:border-white/30"}`}
                      data-testid="paymethod-cash"
                    >
                      💵 {lang === "bg" ? "В брой" : "Cash"}
                    </button>
                  </div>
                  {payMethod === "cash" && (
                    <div className="text-[11px] text-amber-300 font-mono mt-2 flex items-start gap-1.5">
                      <span>⓵</span>
                      <span>{lang === "bg" ? "Плащаш на място на треньора. Резервацията изчаква неговото потвърждение." : "Pay on-site to the coach. The coach must confirm the booking."}</span>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <span className="label-mono">{lang === "bg" ? "Цена" : "Total"}</span>
                <span className="font-oswald text-3xl text-pitch">€{amount.toFixed(2)}</span>
              </div>
              {err && <div className="text-red-400 text-xs font-mono" data-testid="booking-error">{err}</div>}
              <button type="submit" disabled={busy || !slot} className="w-full btn-primary py-3 flex items-center justify-center gap-2" data-testid="booking-submit">
                <Sparkles className="w-4 h-4" />
                {busy
                  ? "..."
                  : payMethod === "cash"
                    ? (lang === "bg" ? "Запази (плащане в брой)" : "Book (Pay cash on-site)")
                    : (lang === "bg" ? "Резервирай & Плати" : "Book & Pay")}
              </button>
              <div className="text-[10px] font-mono text-white/30 text-center">
                {payMethod === "cash"
                  ? (lang === "bg" ? "Без онлайн плащане · Очаквай потвърждение" : "No online payment · Awaiting coach confirmation")
                  : (lang === "bg" ? "Сигурно плащане през Stripe" : "Secure payment via Stripe")}
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
